import { useApolloClient, useSubscription } from '@apollo/client';
import {
  CacheKeys,
  ProjectAppTypes,
  useAppUser,
  useCommodityCache,
  useConfig,
  useEnvProvider,
  USER_PROJECT_ID,
} from '@bechtel/client-common';
import { SUBSCRIPTION_COMMODITIES_UPDATED } from '@bechtel/client-gql';
import { CommoditiesUpdatedPayload, CommodityStatusCode, CrewMember, PersonaType } from '@bechtel/types';
import { every, includes } from 'lodash';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
export function useCommoditySubscription() {
  const { features } = useConfig();
  const { data } = useSubscription<{ commoditiesUpdated: CommoditiesUpdatedPayload }>(SUBSCRIPTION_COMMODITIES_UPDATED);
  const { updateCommoditiesCache } = useCommodityCache();
  const { user } = useAppUser();
  const client = useApolloClient();
  const { envConfig } = useEnvProvider();
  const location = useLocation();
  const dashboardSelectedUser: CrewMember = JSON.parse(sessionStorage.getItem(CacheKeys.DashboardSelectedUser));
  const corePersonaTypes = [PersonaType.SUPERINTENDENT, PersonaType.GENERAL_FOREMAN, PersonaType.FOREMAN];
  const isASAppType = envConfig.appName === ProjectAppTypes.AS;
  const currentUserSelectedProject = window.sessionStorage.getItem(USER_PROJECT_ID);
  useEffect(() => {
    if (
      !data ||
      !data.commoditiesUpdated ||
      !data.commoditiesUpdated.projectId ||
      !currentUserSelectedProject ||
      data.commoditiesUpdated.projectId !== currentUserSelectedProject ||
      !data.commoditiesUpdated.commodityIds ||
      data.commoditiesUpdated.commodityIds.length == 0
    )
      return;
    updateCommoditiesCache(data.commoditiesUpdated.commodityIds);
  }, [data]);

  useEffect(() => {
    //For fitup request not calling any subscription
    if (
      data &&
      data?.commoditiesUpdated &&
      data?.commoditiesUpdated?.weldsCurrentStatus &&
      every(
        data?.commoditiesUpdated?.weldsCurrentStatus,
        (weldStatus) => weldStatus?.status === CommodityStatusCode.FITUP_REQUESTED
      )
    )
      return;

    const pathName = location.pathname;
    const fetchDashboardStatForSuperGfForeman = async () => {
      await client.refetchQueries({
        include: [
          'commodities',
          'commodityStats',
          'commodityStatsPriority',
          'commodityWeeklyStats',
          'crewProgressStats',
          'commodityDashboardTabStats',
        ],
      });
      if (features?.showMapView) {
        await client.refetchQueries({
          include: ['mapMarkers'],
        });
      }
    };

    const fetchDashboardStatForFWE = async () => {
      await client.refetchQueries({
        include: [
          'commodities',
          'commodityStatsClaimedAndUnassignedFWE',
          'commodityStatsRejectedFWE',
          'commodityWeeklyStats',
          'commodityFitupStats',
          'commodityDashboardTabStats',
          'commodityStatsAllPriorityStats',
          'assignToMeStatsFWE',
          'commodityBlocksStatsFWE',
        ],
      });

      if (features?.showMapView) {
        await client.refetchQueries({
          include: ['fweMapMarkers', 'fweFitupMapMarkers', 'fweApprovedMapMarkers'],
        });
      }
    };

    const fetchWeldList = async () => {
      await client.refetchQueries({
        include: ['commodities'],
      });
    };

    if (!data || !data.commoditiesUpdated) return;

    //If not these four persona's Sup/Gf/Foreman/FWE Return
    if (
      !(
        includes([...corePersonaTypes, PersonaType.FIELD_WELD_ENGINEER], user?.crewMember?.persona) ||
        includes([...corePersonaTypes, PersonaType.FIELD_WELD_ENGINEER], dashboardSelectedUser?.persona)
      )
    )
      return;

    const affectedOwnerIds = data?.commoditiesUpdated?.affectedOwnerIds;

    //if the user is Foreman/Gf/Super or SuperAdmin dashboard selected user is Foreman/Gf/Super
    if (
      affectedOwnerIds &&
      (includes(affectedOwnerIds, user?.id) || includes(affectedOwnerIds, dashboardSelectedUser?.id)) &&
      (includes(corePersonaTypes, user?.crewMember?.persona) ||
        includes(corePersonaTypes, dashboardSelectedUser?.persona))
    ) {
      if (isASAppType && pathName && pathName === '/dashboard') {
        //TODO: Stop Dashboard Subscription call to reduce server load for temp time only
        // fetchDashboardStatForSuperGfForeman();
      }
      if (isASAppType && pathName && pathName.includes('/')) {
        fetchWeldList();
      }
    }
    //if the user is FWE or  dashboard selected user is FWE then below queries will refresh
    // here we also checked affectedOwnerId length check it help to differentiate b/w spool commodities and weld commodities because same subscription fire in both case
    //and spool not have any owner but weld commodities atleast have one owner for any activity, so now it will not relaod FWE screen if any activity only on spool from PF side
    if (
      (includes([PersonaType.FIELD_WELD_ENGINEER], user?.crewMember?.persona) ||
        includes([PersonaType.FIELD_WELD_ENGINEER], dashboardSelectedUser?.persona)) &&
      affectedOwnerIds?.length > 0
    ) {
      if (isASAppType && pathName && pathName === '/dashboard') {
        //TODO: Stop Dashboard Subscription call to reduce server load for temp time only
        // fetchDashboardStatForFWE();
      }
      if (isASAppType && pathName && pathName.includes('/')) {
        fetchWeldList();
      }
    }
  }, [location, data, user, dashboardSelectedUser]);

  return null;
}
